import { RepairEstimate, RepairLineItem, ComparisonResult, EstimateDifference, ChangedItem, SummaryDifference } from '@/types/quick-compare';

/**
 * Normalizes a description string for comparison
 * by removing special characters and standardizing spacing
 */
function normalizeDescription(description: string): string {
  return description
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '') // Remove special chars
    .replace(/\s+/g, ' ')        // Standardize spacing
    .trim();
}

/**
 * Calculates similarity between two strings
 * Returns a value between 0 (no similarity) and 1 (identical)
 */
function calculateStringSimilarity(str1: string, str2: string): number {
  const normalized1 = normalizeDescription(str1);
  const normalized2 = normalizeDescription(str2);
  
  if (normalized1 === normalized2) return 1;
  if (!normalized1 || !normalized2) return 0;
  
  // Calculate Levenshtein distance
  const len1 = normalized1.length;
  const len2 = normalized2.length;
  const maxLen = Math.max(len1, len2);
  
  if (maxLen === 0) return 1;
  
  // Use a simple matrix-based approach for Levenshtein distance
  const matrix: number[][] = Array(len1 + 1).fill(null).map(() => Array(len2 + 1).fill(null));
  
  for (let i = 0; i <= len1; i++) matrix[i][0] = i;
  for (let j = 0; j <= len2; j++) matrix[0][j] = j;
  
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = normalized1[i - 1] === normalized2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i-1][j] + 1,      // deletion
        matrix[i][j-1] + 1,      // insertion
        matrix[i-1][j-1] + cost  // substitution
      );
    }
  }
  
  const distance = matrix[len1][len2];
  return 1 - (distance / maxLen);
}

/**
 * Generates a unique key for a repair line item
 * using operation code, normalized description and category information
 * but NOT quantity (to catch quantity changes)
 */
function generateLineItemKey(item: RepairLineItem): string {
  const normalizedDesc = normalizeDescription(item.description);
  const opCode = item.operationCode || '';
  const laborType = item.laborType || '';
  
  // No longer using quantity in key to ensure we catch quantity changes
  return `${opCode}-${normalizedDesc}-${laborType}`;
}

/**
 * Finds the best match for an item in a collection based on string similarity
 */
function findBestMatch(
  item: RepairLineItem, 
  collection: Map<string, { item: RepairLineItem, section: string }>,
  threshold = 0.8
): { match: { item: RepairLineItem, section: string } | null, similarity: number } {
  let bestMatch = null;
  let bestSimilarity = 0;
  
  const itemDesc = normalizeDescription(item.description);
  
  collection.forEach((value) => {
    const otherDesc = normalizeDescription(value.item.description);
    const similarity = calculateStringSimilarity(itemDesc, otherDesc);
    
    if (similarity > bestSimilarity && similarity >= threshold) {
      bestMatch = value;
      bestSimilarity = similarity;
    }
  });
  
  return { match: bestMatch, similarity: bestSimilarity };
}

/**
 * Compare two estimates and identify all differences with improved detection for all field types
 */
export function compareEstimates(
  original: RepairEstimate,
  modified: RepairEstimate
): ComparisonResult {
  console.log('Starting enhanced estimate comparison');
  
  // Log input data for debugging
  console.log('Original estimate:', {
    sections: Object.keys(original.sections),
    totalLineItems: Object.values(original.sections).flat().length,
    totalAmount: original.totalAmount,
    summary: original.summary
  });
  console.log('Modified estimate:', {
    sections: Object.keys(modified.sections),
    totalLineItems: Object.values(modified.sections).flat().length,
    totalAmount: modified.totalAmount,
    summary: modified.summary
  });
  
  const differences: EstimateDifference[] = [];
  const summaryDifferences: SummaryDifference[] = [];
  
  // Track sections for easier access
  const originalSections = original.sections;
  const modifiedSections = modified.sections;
  
  // Create maps of line items by their keys for faster lookup
  const originalLineItems = new Map<string, { item: RepairLineItem, section: string }>();
  const modifiedLineItems = new Map<string, { item: RepairLineItem, section: string }>();
  
  // Create separate maps for fuzzy matching
  const originalLineItemsForFuzzy = new Map<string, { item: RepairLineItem, section: string }>();
  const modifiedLineItemsForFuzzy = new Map<string, { item: RepairLineItem, section: string }>();
  
  // Track which items have been matched via fuzzy matching to avoid duplicates
  const matchedOriginalItems = new Set<string>();
  const matchedModifiedItems = new Set<string>();

  // Populate the original map
  Object.entries(originalSections).forEach(([section, items]) => {
    items.forEach((item) => {
      // Add line number to each item for better tracking if not present
      if (!item.lineNumber && item.operationCode) {
        item.lineNumber = item.operationCode;
      }
      
      const key = generateLineItemKey(item);
      originalLineItems.set(key, { item, section });
      originalLineItemsForFuzzy.set(item.description, { item, section });
    });
  });

  // Populate the modified map and track added items
  Object.entries(modifiedSections).forEach(([section, items]) => {
    items.forEach((item) => {
      // Add line number to each item for better tracking if not present
      if (!item.lineNumber && item.operationCode) {
        item.lineNumber = item.operationCode;
      }
      
      const key = generateLineItemKey(item);
      modifiedLineItems.set(key, { item, section });
      modifiedLineItemsForFuzzy.set(item.description, { item, section });
      
      if (!originalLineItems.has(key)) {
        // Check if this could be a fuzzy match before marking it as added
        const { match, similarity } = findBestMatch(item, originalLineItems);
        
        if (match && similarity >= 0.8) {
          // This is a fuzzy match - mark as changed rather than added
          matchedOriginalItems.add(generateLineItemKey(match.item));
          matchedModifiedItems.add(key);
          
          differences.push({
            type: 'changed',
            path: [section, 'items'],
            lineItem: { ...item },
            originalLineItem: { ...match.item },
            section,
            fuzzyMatch: true,
            similarity
          });
        } else {
          // This is truly a new item
          differences.push({
            type: 'added',
            path: [section, 'items'],
            lineItem: { ...item },
            section
          });
        }
      }
    });
  });

  // Find removed and changed items
  originalLineItems.forEach(({ item: originalItem, section }, key) => {
    // Skip items already processed via fuzzy matching
    if (matchedOriginalItems.has(key)) return;
    
    if (!modifiedLineItems.has(key)) {
      // Check if this could be a fuzzy match before marking it as removed
      const { match, similarity } = findBestMatch(originalItem, modifiedLineItems);
      
      if (match && similarity >= 0.8 && !matchedModifiedItems.has(generateLineItemKey(match.item))) {
        // This is a fuzzy match - mark as changed rather than removed
        matchedModifiedItems.add(generateLineItemKey(match.item));
        
        differences.push({
          type: 'changed',
          path: [section, 'items'],
          lineItem: { ...match.item },
          originalLineItem: { ...originalItem },
          section,
          fuzzyMatch: true,
          similarity
        });
      } else {
        // This item was truly removed
        differences.push({
          type: 'removed',
          path: [section, 'items'],
          lineItem: { ...originalItem },
          section
        });
      }
    } else {
      // Item exists in both, check for changes in all properties
      const { item: modifiedItem } = modifiedLineItems.get(key)!;
      
      // Track which fields have changed for better UI display
      const changedFields: string[] = [];
      
      // Compare properties that might change
      const propsToCompare: (keyof RepairLineItem)[] = [
        'quantity', 'price', 'totalPrice', 'laborHours', 'laborRate', 'laborType',
        'partType', 'description'
      ];
      
      for (const prop of propsToCompare) {
        // Special handling for part types - ensure even OEM parts are compared properly
        if (prop === 'partType') {
          // Even if both are undefined or null, we should compare them as strings
          const originalPartType = originalItem[prop]?.toString() || '';
          const modifiedPartType = modifiedItem[prop]?.toString() || '';
          
          console.log(`Comparing part types: "${originalPartType}" vs "${modifiedPartType}"`);
          
          if (originalPartType !== modifiedPartType) {
            changedFields.push(prop as string);
            
            differences.push({
              type: 'changed',
              path: [section, 'items', prop as string],
              lineItem: { ...modifiedItem },
              originalValue: originalPartType,
              newValue: modifiedPartType,
              section,
              originalLineItem: { ...originalItem },
              changeType: prop as string
            });
          }
        } 
        // Enhanced labor hours comparison to detect even small differences
        else if (prop === 'laborHours' && typeof originalItem[prop] === 'number' && typeof modifiedItem[prop] === 'number') {
          // Use a small epsilon value for floating point comparison
          const epsilon = 0.001;
          const areHoursDifferent = Math.abs((originalItem[prop] as number) - (modifiedItem[prop] as number)) > epsilon;
          
          if (areHoursDifferent) {
            changedFields.push(prop as string);
            
            differences.push({
              type: 'changed',
              path: [section, 'items', prop as string],
              lineItem: { ...modifiedItem },
              originalValue: originalItem[prop],
              newValue: modifiedItem[prop],
              section,
              originalLineItem: { ...originalItem },
              changeType: prop as string
            });
          }
        }
        // Default comparison for other properties
        else if (originalItem[prop] !== modifiedItem[prop]) {
          changedFields.push(prop as string);
          
          differences.push({
            type: 'changed',
            path: [section, 'items', prop as string],
            lineItem: { ...modifiedItem },
            originalValue: originalItem[prop],
            newValue: modifiedItem[prop],
            section,
            originalLineItem: { ...originalItem },
            changeType: prop as string
          });
        }
      }
    }
  });
  
  // Compare summary information if available
  if (original.summary && modified.summary) {
    // Compare parts total
    if (original.summary.partsTotal !== modified.summary.partsTotal) {
      summaryDifferences.push({
        category: 'Parts',
        field: 'partsTotal',
        originalValue: original.summary.partsTotal,
        newValue: modified.summary.partsTotal,
        difference: modified.summary.partsTotal - original.summary.partsTotal
      });
    }
    
    // Compare labor body
    if (original.summary.labor?.body !== modified.summary.labor?.body) {
      summaryDifferences.push({
        category: 'Labor',
        field: 'bodyLabor',
        originalValue: original.summary.labor?.body || 0,
        newValue: modified.summary.labor?.body || 0,
        difference: (modified.summary.labor?.body || 0) - (original.summary.labor?.body || 0)
      });
    }
    
    // Compare labor body hours - improved detection
    if (original.summary.labor?.bodyHours !== modified.summary.labor?.bodyHours) {
      // Use a small epsilon for floating point comparison
      const epsilon = 0.001;
      const originalHours = original.summary.labor?.bodyHours || 0;
      const modifiedHours = modified.summary.labor?.bodyHours || 0;
      
      if (Math.abs(originalHours - modifiedHours) > epsilon) {
        summaryDifferences.push({
          category: 'Labor Hours',
          field: 'bodyHours',
          originalValue: originalHours,
          newValue: modifiedHours,
          difference: modifiedHours - originalHours
        });
      }
    }
    
    // Compare labor refinish
    if (original.summary.labor?.refinish !== modified.summary.labor?.refinish) {
      summaryDifferences.push({
        category: 'Labor',
        field: 'refinishLabor',
        originalValue: original.summary.labor?.refinish || 0,
        newValue: modified.summary.labor?.refinish || 0,
        difference: (modified.summary.labor?.refinish || 0) - (original.summary.labor?.refinish || 0)
      });
    }
    
    // Compare labor refinish hours - improved detection
    if (original.summary.labor?.refinishHours !== modified.summary.labor?.refinishHours) {
      // Use a small epsilon for floating point comparison
      const epsilon = 0.001;
      const originalHours = original.summary.labor?.refinishHours || 0;
      const modifiedHours = modified.summary.labor?.refinishHours || 0;
      
      if (Math.abs(originalHours - modifiedHours) > epsilon) {
        summaryDifferences.push({
          category: 'Labor Hours',
          field: 'refinishHours',
          originalValue: originalHours,
          newValue: modifiedHours,
          difference: modifiedHours - originalHours
        });
      }
    }
    
    // Compare mechanical labor hours - added new comparison
    if (original.summary.labor?.mechanicalHours !== modified.summary.labor?.mechanicalHours) {
      summaryDifferences.push({
        category: 'Labor Hours',
        field: 'mechanicalHours',
        originalValue: original.summary.labor?.mechanicalHours || 0,
        newValue: modified.summary.labor?.mechanicalHours || 0,
        difference: (modified.summary.labor?.mechanicalHours || 0) - (original.summary.labor?.mechanicalHours || 0)
      });
    }

    // Compare total labor hours - added new comparison with improved precision
    const originalTotalHours = (original.summary.labor?.bodyHours || 0) + 
                             (original.summary.labor?.refinishHours || 0) + 
                             (original.summary.labor?.mechanicalHours || 0);
    
    const modifiedTotalHours = (modified.summary.labor?.bodyHours || 0) + 
                             (modified.summary.labor?.refinishHours || 0) + 
                             (modified.summary.labor?.mechanicalHours || 0);
    
    const hoursDelta = Math.abs(originalTotalHours - modifiedTotalHours);
    console.log(`Total labor hours comparison: ${originalTotalHours.toFixed(1)} vs ${modifiedTotalHours.toFixed(1)}, delta: ${hoursDelta.toFixed(3)}`);
    
    // Use an epsilon of 0.01 hours (less than a minute) to avoid floating point issues
    if (hoursDelta > 0.01) {
      summaryDifferences.push({
        category: 'Labor Hours',
        field: 'totalHours',
        originalValue: originalTotalHours,
        newValue: modifiedTotalHours,
        difference: modifiedTotalHours - originalTotalHours
      });
    }
    
    // Compare materials
    if (original.summary.materials?.paint !== modified.summary.materials?.paint) {
      summaryDifferences.push({
        category: 'Materials',
        field: 'paintMaterials',
        originalValue: original.summary.materials?.paint || 0,
        newValue: modified.summary.materials?.paint || 0,
        difference: (modified.summary.materials?.paint || 0) - (original.summary.materials?.paint || 0)
      });
    }
    
    // Compare subtotal
    if (original.summary.subtotal !== modified.summary.subtotal) {
      summaryDifferences.push({
        category: 'Totals',
        field: 'subtotal',
        originalValue: original.summary.subtotal,
        newValue: modified.summary.subtotal,
        difference: modified.summary.subtotal - original.summary.subtotal
      });
    }
    
    // Compare sales tax
    if (original.summary.salesTax !== modified.summary.salesTax) {
      summaryDifferences.push({
        category: 'Totals',
        field: 'salesTax',
        originalValue: original.summary.salesTax,
        newValue: modified.summary.salesTax,
        difference: modified.summary.salesTax - original.summary.salesTax
      });
    }
    
    // Compare grand total
    if (original.summary.grandTotal !== modified.summary.grandTotal) {
      summaryDifferences.push({
        category: 'Totals',
        field: 'grandTotal',
        originalValue: original.summary.grandTotal,
        newValue: modified.summary.grandTotal,
        difference: modified.summary.grandTotal - original.summary.grandTotal
      });
    }
  }
  
  // Calculate actual total amounts based on line items for better comparison
  let originalCalculatedTotal = 0;
  let modifiedCalculatedTotal = 0;
  
  Object.values(originalSections).flat().forEach(item => {
    if (item.totalPrice) originalCalculatedTotal += item.totalPrice;
    else if (item.price && item.quantity) originalCalculatedTotal += (item.price * item.quantity);
  });
  
  Object.values(modifiedSections).flat().forEach(item => {
    if (item.totalPrice) modifiedCalculatedTotal += item.totalPrice;
    else if (item.price && item.quantity) modifiedCalculatedTotal += (item.price * item.quantity);
  });
  
  // Use calculated totals if the provided ones are 0 or null
  if (!original.totalAmount || original.totalAmount === 0) {
    original.totalAmount = originalCalculatedTotal;
  }
  
  if (!modified.totalAmount || modified.totalAmount === 0) {
    modified.totalAmount = modifiedCalculatedTotal;
  }
  
  // If summary totals don't match calculated totals but we didn't find summary differences
  if (summaryDifferences.length === 0 && original.totalAmount !== modified.totalAmount) {
    summaryDifferences.push({
      category: 'Calculated Totals',
      field: 'estimateTotal',
      originalValue: original.totalAmount,
      newValue: modified.totalAmount,
      difference: modified.totalAmount - original.totalAmount
    });
  }
  
  // Count the different types of changes
  const addedItems = differences.filter(d => d.type === 'added').length;
  const removedItems = differences.filter(d => d.type === 'removed').length;
  const changedItems = differences.filter(d => d.type === 'changed' && !d.fuzzyMatch).length;
  const fuzzyChangedItems = differences.filter(d => d.type === 'changed' && d.fuzzyMatch).length;
  
  // Create a summary of all changes
  const summary = {
    addedItems,
    removedItems,
    changedItems: changedItems + fuzzyChangedItems, // Include fuzzy matches in the change count
    priceDifference: modified.totalAmount - original.totalAmount
  };

  // Determine if the estimates are identical
  const PRICE_THRESHOLD = 0.01;  // 1 cent threshold
  const hasPriceDifference = Math.abs(summary.priceDifference) > PRICE_THRESHOLD;
  const isIdentical = differences.length === 0 && summaryDifferences.length === 0 && !hasPriceDifference;

  const result: ComparisonResult = {
    isIdentical,
    differences,
    summaryDifferences,
    original,
    modified,
    summary,
    comparisonDate: new Date().toISOString()
  };
  
  console.log('Comparison results:', {
    isIdentical,
    addedItems,
    removedItems, 
    changedItems,
    fuzzyChangedItems,
    priceDifference: summary.priceDifference.toFixed(2),
    totalDifferences: differences.length,
    summaryDifferencesCount: summaryDifferences.length,
    originalCalculatedTotal,
    modifiedCalculatedTotal
  });
  
  return result;
}

/**
 * Groups differences by section
 */
export function groupDifferencesBySection(differences: EstimateDifference[]): Record<string, any> {
  return differences.reduce((acc: Record<string, any>, diff: EstimateDifference) => {
    const section = diff.section || 'General';
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(diff);
    return acc;
  }, {});
}

/**
 * Calculates section-level statistics for comparison results
 */
export function calculateSectionStatistics(groupedDifferences: Record<string, any>): Record<string, any> {
  const sectionStats: Record<string, any> = {};

  for (const section in groupedDifferences) {
    const differences = groupedDifferences[section];
    
    // Calculate price difference for this section
    let sectionPriceDifference = 0;
    
    differences.forEach((d: EstimateDifference) => {
      if (d.type === 'added' && d.lineItem?.price && d.lineItem?.quantity) {
        sectionPriceDifference += (d.lineItem.price * d.lineItem.quantity);
      } else if (d.type === 'removed' && d.lineItem?.price && d.lineItem?.quantity) {
        sectionPriceDifference -= (d.lineItem.price * d.lineItem.quantity);
      } else if (d.type === 'changed') {
        if (d.path.includes('price') && typeof d.originalValue === 'number' && typeof d.newValue === 'number') {
          const qty = d.lineItem?.quantity || 1;
          sectionPriceDifference += ((d.newValue - d.originalValue) * qty);
        } else if (d.path.includes('quantity') && typeof d.originalValue === 'number' && typeof d.newValue === 'number') {
          const price = d.lineItem?.price || 0;
          sectionPriceDifference += ((d.newValue - d.originalValue) * price);
        }
      }
    });
    
    sectionStats[section] = {
      added: differences.filter((d: EstimateDifference) => d.type === 'added').length,
      removed: differences.filter((d: EstimateDifference) => d.type === 'removed').length,
      changed: differences.filter((d: EstimateDifference) => d.type === 'changed').length,
      priceDifference: sectionPriceDifference
    };
  }

  return sectionStats;
}

/**
 * Formats line items for display in the UI, grouping added, removed, and changed items
 * Now with improved detection of different change types
 */
export function formatLineItemsForDisplay(differences: EstimateDifference[]) {
  // Collection for added and removed items
  const added = differences.filter(d => d.type === 'added')
    .map(d => ({ ...d.lineItem, section: d.section }));
  
  const removed = differences.filter(d => d.type === 'removed')
    .map(d => ({ ...d.lineItem, section: d.section }));

  // Format changed items according to ChangedItem interface structure with improved change tracking
  const changed = differences.filter(d => d.type === 'changed')
    .reduce((acc: ChangedItem[], diff) => {
      // Skip partial property change records, only process the first one for each item
      if (!diff.originalLineItem || !diff.lineItem) return acc;
      
      // Check if we already have a change for this item to avoid duplicates
      const existingChange = acc.find(c => 
        c.original.description === diff.originalLineItem?.description &&
        c.original.lineNumber === diff.originalLineItem?.lineNumber
      );
      
      if (!existingChange) {
        // Create a new changed item with the original and modified values
        // Making sure to preserve part type and other important fields
        acc.push({
          original: { ...diff.originalLineItem, section: diff.section },
          modified: { ...diff.lineItem, section: diff.section },
          similarity: diff.fuzzyMatch ? (diff.similarity || 1.0) : 1.0,
          fuzzyMatch: diff.fuzzyMatch || false,
          changedFields: diff.changeType ? [diff.changeType] : [] // Initialize with the current change
        });
      } else if (diff.changeType) {
        // Record which specific field changed in the existing change object
        if (!existingChange.changedFields) {
          existingChange.changedFields = [];
        }
        
        if (!existingChange.changedFields.includes(diff.changeType)) {
          existingChange.changedFields.push(diff.changeType);
        }
      }
      
      return acc;
    }, []);

  // Add additional check for changes in part type fields that might be missed
  changed.forEach(item => {
    // Log part types for debugging
    console.log('Checking part type difference:', {
      description: item.original.description,
      originalPartType: item.original.partType,
      modifiedPartType: item.modified.partType
    });
    
    // Check part type differences, ensuring string comparison
    const originalPartType = item.original.partType?.toString() || '';
    const modifiedPartType = item.modified.partType?.toString() || '';
    
    if (originalPartType !== modifiedPartType) {
      if (!item.changedFields) {
        item.changedFields = [];
      }
      if (!item.changedFields.includes('partType')) {
        item.changedFields.push('partType');
        console.log(`Added partType to changedFields for ${item.original.description}`);
      }
    }
  });
  
  return {
    added,
    removed,
    changed
  };
}
